<template>
  <div id="keyword-history" class="keyword-history">
    <div class="keyword-history__header">
      <div class="keyword-history__header_main-info" ref="main-info-header">
        <base-search-in-google class="keyword-history__title__search" :query="title" />
        <base-truncate-label class="keyword-history__title"
                             :style="{maxWidth: `${headerKeywordMaxWidth}px`}"
                             :shrink="1"
                             :label="title"
                             watchClass="keyword-history__title"/>
        <serp-feature-icons v-if="serpFeatures.length"
                            ref="serp"
                            class="keyword-history__serp-features"
                            :featureList="serpFeatures"/>
      </div>
      <div class="keyword-history__header_main-info keyword-history__header_url-info">
        <div v-for="data in keywordData"
             :key="data[0]"
             class="keyword-history__keyword-data">
          <div class="keyword-history__keyword-data_header">{{ data[0] }}:</div>
          <div class="keyword-history__keyword-data_count">{{ data[1] }}</div>
        </div>
      </div>
      <div class="keyword-history__separator"></div>
      <div class="keyword-history__header_main-info keyword-history__header_main-info_column keyword-history__header_keyword-snapshot-data">
        <div class="keyword-history__header_main-info_url-data_wrapper">
          <span ref="status" class="keyword-history__header_main-info_url-data">URL Status: {{urlStatus}}</span>
          <router-link :to="`/content-briefs/${contentBriefId}/${isPublished ? 'progress' : 'strategy'}`"
                       :style="{left: cbIconLeft}"
                       v-if="!isScreenshotTaking"
                       class="keyword-history__open-content-brief" target="_blank">
            <base-material-design-icon class=""
                                       icon="file"/>
          </router-link>
        </div>
        <div class="keyword-history__keyword-data_select_wrapper">
          <div class="keyword-history__keyword-data keyword-history__keyword-data_select">
            <div class="keyword-history__keyword-data_header">
              Mapped URL
                <a v-if="isPublished"
                   class="keyword-history__keyword-data_mapped-url-redirect"
                   :href="mappedUrl" target="_blank">
                  <base-material-design-icon icon="linkBox" />
                </a>
            </div>
            <base-select
              class="keyword-history__web-property-select keyword-history__web-property-select_url"
              truncateLabel
              isSmartInput
              :titleStyle="{color: '#011627', paddingLeft: '15px'}"
              :options="mappedUrlOptions"
              v-model="mappedUrlId"
              showTopBorder
              :disabled="isCurrentUserClientCollaborator"
              :propListStyle="{ maxHeight: '360px', maxWidth: '400px' }"
              placeholder="Mapped URL"/>
          </div>
          <div :class="{'keyword-history__keyword-data_score': !isClientDifficultyScoreEnabled}"
               class="keyword-history__keyword-data keyword-history__keyword-data_select">
            <div class="keyword-history__keyword-data_header">Rel Score</div>
            <base-select
              class="keyword-history__web-property-select keyword-history__web-property-select_score"
              truncateLabel
              :titleStyle="{color: '#011627', paddingLeft: '15px'}"
              :options="relevancyOptions"
              v-model="relevancy"
              showTopBorder
              :disabled="isCurrentUserClientCollaborator"
              :propListStyle="{ maxHeight: '360px', maxWidth: '80px' }"
              placeholder="Rel Score"/>
          </div>
          <div v-if="isClientDifficultyScoreEnabled"
               class="keyword-history__keyword-data keyword-history__keyword-data_diff-score keyword-history__keyword-data_select">
            <div class="keyword-history__keyword-data_header">Diff Score</div>
            <base-page-title
              :title="diffScore"
              :disabled="isCurrentUserClientCollaborator"
              type="number"
              @change-slug-name="updateKeywordUrlAndScore('diffScore', $event)"
              class="keyword-history__diff-score" />
          </div>
          <div class="keyword-history__keyword-data keyword-history__keyword-data_select">
            <div class="keyword-history__keyword-data_header">Primary</div>
            <base-select
              class="keyword-history__web-property-select"
              truncateLabel
              :titleStyle="{color: '#011627', paddingLeft: '15px'}"
              :options="webPropertyGroupsOptions"
              v-model="webPropertyGroupId"
              showTopBorder
              :propListStyle="{ maxHeight: '360px', maxWidth: '200px' }"
              placeholder="Web Property"/>
          </div>
          <div class="keyword-history__keyword-data keyword-history__keyword-data_select">
            <div class="keyword-history__keyword-data_header">
              Comparison
            </div>
            <base-multi-select
              class="keyword-history__multi-select"
              truncateLabel
              :titleStyle="{fontWeight: 'normal', fontSize: '14px', textTransform: 'none', color: isComparing ? '#011627' : '#7a8894'}"
              showSelected
              useCheckBox
              disableAllOption
              countOptions
              allLabel
              enableRemoveOption
              optionColor="#011627"
              :maxHeightList="360"
              :options="webPropertyGroupsOptions"
              title="Select"
              v-model="selectedComparisonWebPropertyGroupIds"
            />
          </div>
        </div>
      </div>
      <div class="keyword-history__buttons">
        <base-material-design-icon class="keyword-history__copy" @click="copyModal" icon="share" />
        <base-screenshot :id="'keyword-history'"
                       :name="screenshotName"
                       class="keyword-history__screenshot"
                       @screenshot="onScreenShotStatusChanged"/>
        <base-material-design-icon v-if="!isScreenshotTaking && !notEnoughData" icon="download" @click="exportCsv()"/>
      </div>
    </div>
    <div v-if="notEnoughData && isDataFetched" class="keyword-history__no-data">Not enough data for drawing a chart</div>
    <div v-else-if="isDataFetched"
         class="keyword-history__diagram">
      <GChart
        :settings="{packages: ['corechart']}"
        :version="'42'"
        :createChart="createChart"
        class="keyword-history__scatter-chart"
      />
    </div>
    <div v-else
         class="keyword-history__loader-container"
    >
      <div class="keyword-history__loader">
        <base-loader/>
      </div>
    </div>
    <div v-if="isDataFetched" class="keyword-history__checkboxes">
      <base-check-box v-for="item in rankZones"
                      class="keyword-history__checkbox"
                      :class="{'keyword-history__checkbox-disabled': disabledCheckboxes.includes(item)}"
                      :label="item"
                      v-model="selectedRankZones[item]"
                      :key="item">
      </base-check-box>
    </div>
    <template v-if="!notEnoughData">
      <div style="margin-top: 15px;"
           class="keyword-history__links keyword-history__links_row"
           :class="{'keyword-history__links_comparing': isComparing}">
        <span class="keyword-history__link_header">Primary</span>
        <span v-if="isComparing"
              :style="{paddingRight: comparisonPaddingRight}"
              class="keyword-history__link_header">
          Comparison
        </span>
      </div>

      <div class="keyword-history__links keyword-history__links_row"
           :class="{'keyword-history__links_comparing': isComparing}">

        <div class="keyword-history__links">
          <div
            v-for="(item, index) in primaryColumns"
            :key="item"
            class="keyword-history__link"
          >
            <div :style="{'background-color': pointColors[index]}" class="keyword-history__link__color"></div>
            <a v-if="!['nr', 'nd'].includes(item.toLowerCase()) && selectedComparisonWebPropertyGroupIds.length < 2" :href="item"
               target="_blank">{{item}}</a>
            <span v-else>{{ item }}</span>
          </div>
        </div>

        <div v-if="isComparing" class="keyword-history__links" v-resize @resize="handleLinksWrapperResize">
          <div
            v-for="(item, index) in compareColumns"
            :key="item"
            class="keyword-history__link"
          >
            <div
              :style="{'background-color': pointColors[primaryColumns.length + (selectedComparisonWebPropertyGroupIds.length === 1 ? 1 : 0) + index]}"
              class="keyword-history__link__color"></div>
            <a v-if="!['nr', 'nd'].includes(item.toLowerCase()) && selectedComparisonWebPropertyGroupIds.length < 2" :href="item"
               target="_blank">{{item}}</a>
            <span v-else>{{item}}</span>
          </div>
        </div>

      </div>
    </template>

    <button class="keyword-history__button keyword-history__button_close"
            v-if="!isScreenshotTaking"
            @click.prevent="close">
      Close
    </button>

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import csvExport from '@/services/CSVExport'
import _ from 'lodash'
import { GChart } from 'vue-google-charts'
import moment from 'moment'
import BaseModalMixins from '@/common/BaseModalMixins'
import SerpFeatureIcons from '@/features/content-briefs/brief-content/SerpFeatureIcons'
import ToasterService from '@/services/toasterService'

// const RANK_ZONES = ['01 - 03', '04 - 07', '08 - 10', '11 - 25', '26 - 50', '51 - 100', 'NR', 'ND']
const RANK_ZONES = ['01 - 03', '04 - 07', '08 - 10', '11 - 25', '26 - 50', '51 - 100', 'NR']
const RELEVANCY_OPTIONS = [
  {
    label: 'Not Set',
    value: '-1'
  },
  {
    label: '0',
    value: '0'
  },
  {
    label: '1',
    value: '1'
  },
  {
    label: '2',
    value: '2'
  },
  {
    label: '3',
    value: '3'
  },
  {
    label: '4',
    value: '4'
  },
  {
    label: '5',
    value: '5'
  }
]
const DEFAULT_SELECTED_RANK_ZONES = {}
RANK_ZONES.forEach(v => { DEFAULT_SELECTED_RANK_ZONES[v] = false })
const POINT_COLORS = ['#2EC4B6', '#284AFF', '#ff862c', '#f2d23e',
  '#008041', '#9025fd', '#53352f', '#a065ff',
  '#c61852', '#00f362', '#c95818', '#c68088',
  '#937979'
]

const ND_COLLOR = '#666'
export default {
  name: 'BaseKeywordHistoryModal',
  mixins: [BaseModalMixins],
  components: {
    GChart,
    SerpFeatureIcons
  },
  created () {
    window.addEventListener('resize', this.resizeListener)
    this.webPropertyGroupsOptions = _.cloneDeep(this.webPropertyGroups).sort((a, b) => {
      if ((a.type === 'Clients' && b.type === 'Siblings') || (a.type === 'Siblings' && b.type === 'Competitors')) {
        return -1
      } else if ((b.type === 'Clients' && a.type === 'Siblings') || (b.type === 'Siblings' && a.type === 'Competitors')) {
        return 1
      } else {
        return 0
      }
    }).map(item => {
      return {
        value: item._id,
        label: item.label,
        type: item.type,
        attributes: item.type === 'Clients' ? [{ 'private-content': true }] : [],
        style: {
          color: item.type === 'Siblings' ? '#284aff' : '#011627',
          fontWeight: item.type === 'Clients' ? 'bold' : 'normal'
        }
      }
    }) || []
  },
  beforeDestroy () {
    this.resetKeywordHistory()
    window.removeEventListener('resize', this.resizeListener)
  },
  props: {
    /**
     * selected keyword
     */
    keyword: {
      type: String,
      required: true
    },
    isFilter: {
      type: Boolean,
      default: false
    },
    predefinedWebPropertyGroupId: {
      type: String,
      default: ''
    },
    predefinedSelectedRankZones: {
      type: Object,
      default: () => _.cloneDeep(DEFAULT_SELECTED_RANK_ZONES)
    },
    /**
     * Predefined comparison webproperty groups
     */
    comparison: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      pointColors: [...POINT_COLORS],
      google: null,
      el: null,
      chartDrawed: false,
      webPropertyGroupsOptions: [],
      isScreenshotTaking: false,
      isFullUpdate: false,
      isDataFetched: false,
      isDataUpdating: false,
      isExportCSV: false,
      chart: null,
      isDataUpdated: false,
      relevancyOptions: _.cloneDeep(RELEVANCY_OPTIONS),
      relevancy: '',
      mappedUrlId: '',
      comparisonPaddingRight: '0px',
      cbIconLeft: '160px',
      headerKeywordMaxWidth: 'auto',
      webPropertyGroupId: null,
      selectedComparisonWebPropertyGroupIds: _.cloneDeep(this.comparison),
      debounced: _.throttle(() => {
        this.createChart(this.el, this.google)
        this.recalculateKeywordMaxWidth()
      }, 100, { leading: true, trailing: true }),
      rankZones: [...RANK_ZONES],
      disabledCheckboxes: [...RANK_ZONES],
      selectedRankZones: _.cloneDeep(this.predefinedSelectedRankZones)
    }
  },
  watch: {
    selectedRankZones: {
      handler (value, prevValue) {
        if (Object.values(prevValue).includes(true) && this.google) {
          this.createChart(this.el, this.google)
        }
      },
      deep: true
    },
    webPropertyGroupId () {
      this.isFullUpdate = true
      this.pointColors = [...POINT_COLORS]
    },
    relevancy (value, prevValue) {
      if (value && prevValue && prevValue !== value) {
        this.updateKeywordUrlAndScore('score')
      }
    },
    async mappedUrlId (value, prevValue) {
      if (value && prevValue && prevValue !== value && prevValue !== -1) {
        // test for prevValue != -1 to prevent second api call when selecting Remove Url
        await this.updateKeywordUrlAndScore('url')
        await this.loadData()
      }
    },
    selectedComparisonWebPropertyGroupIds: {
      handler () {
        this.isFullUpdate = true
        this.pointColors = [...POINT_COLORS]
      },
      deep: true
    },
    urlStatus () {
      this.$nextTick(() => {
        this.cbIconLeft = this.$refs.status ? `${this.$refs.status.clientWidth + 15}px` : '160px'
      })
    },
    serpFeatures: {
      handler () {
        this.recalculateKeywordMaxWidth()
      },
      deep: true,
      immediate: true
    },
    webPropertyGroupsOptions: {
      handler (groups) {
        if (groups.length) {
          /* set default webPropertyGroup */
          this.webPropertyGroupId = this.predefinedWebPropertyGroupId && this.predefinedWebPropertyGroupId.length
            ? this.predefinedWebPropertyGroupId
            : groups[0].value
          this.loadData(true)
        }
      },
      deep: true
    },
    chartData: {
      handler () {
        this.createChart(this.el, this.google)
      },
      deep: true
    }
  },
  computed: {
    ...mapState('rankZoneCommon', ['webPropertyGroups', 'mappedUrls', 'webProperties']),
    ...mapState('rankZoneKeywordMovementHistory', {
      chartData (state) {
        if (!state.keywordHistory || !state.keywordHistory.length) {
          return null
        }
        if (this.isFullUpdate && !this.isDataUpdating) {
          this.disabledCheckboxes = [...RANK_ZONES]
          !this.isFilter && (this.selectedRankZones = { ...DEFAULT_SELECTED_RANK_ZONES })
        }
        const keywordHistory = _.cloneDeep(
          state.keywordHistory
        ).filter(
          i => this.webPropertyGroupId === i.web_property || this.selectedComparisonWebPropertyGroupIds.includes(i.web_property)
        ).sort((a, b) => {
          const snapshotDate1 = moment(a.snapshot_date).toDate()
          const snapshotDate2 = moment(b.snapshot_date).toDate()
          if (snapshotDate1 < snapshotDate2) {
            return -1
          }
          if (snapshotDate1 > snapshotDate2) {
            return 1
          }
          return 0
        })

        const getRankingUrl = (rank, url) => {
          switch (true) {
            case (
              (rank <= 100 && rank >= 1) ||
              (rank > 100 && rank !== 404 && url) ||
              (rank === 404 && url)
            ):
              return url
            case rank === 404:
              return 'ND'
            default:
              return 'NR'
          }
        }

        const getRankZone = (rank) => {
          switch (true) {
            case rank <= 3:
              return '01 - 03'
            case rank <= 7:
              return '04 - 07'
            case rank <= 10:
              return '08 - 10'
            case rank <= 25:
              return '11 - 25'
            case rank <= 50:
              return '26 - 50'
            case rank <= 100:
              return '51 - 100'
            case rank === 404:
              return 'ND'
            default:
              return 'NR'
          }
        }

        let minRank = 100
        let maxRank = 1
        let notRankingEnabled = false
        let noDataEnabled = false

        keywordHistory.forEach(item => {
          if (this.google && !this.isFullUpdate && !this.isDataUpdating) {
            const rankZone = getRankZone(item.rank)
            if (!this.selectedRankZones[rankZone]) return
          }
          if ((item.rank > 100 && item.rank !== 404) || item.rank < 1) {
            notRankingEnabled = true
            return
          }
          if (item.rank === 404) {
            noDataEnabled = true
            return
          }
          if (item.rank > maxRank) maxRank = item.rank
          if (item.rank < minRank) minRank = item.rank
        })

        minRank = minRank === 100 ? 1 : minRank - minRank % 10
        maxRank = maxRank + (maxRank % 10 ? 10 - maxRank % 10 : 0)

        const yTicks = []
        for (let i = minRank; i <= maxRank; i += 10) {
          i ? yTicks.push(i) : yTicks.push(1)
        }
        notRankingEnabled && yTicks.push({ v: maxRank + 10, f: 'NR' })
        noDataEnabled && yTicks.push({ v: maxRank + (notRankingEnabled ? 20 : 10), f: 'ND' })

        let xTicks = []
        const data = []
        let columns = []
        const objectOfWebproperties = {}
        let mappedColumns = [[], []]
        const valuesToEnd = ['ND', 'NR']
        function sortColumns (a, b) {
          if ((a === 'ND' && b === 'NR') || (valuesToEnd.includes(a) && !valuesToEnd.includes(b))) {
            return 1
          } else if ((a === 'NR' && b === 'ND') || (valuesToEnd.includes(b) && !valuesToEnd.includes(a))) {
            return -1
          } else {
            return 0
          }
        }
        if (!this.isComparing) {
          keywordHistory.forEach(item => {
            const rankZone = getRankZone(item.rank)

            if (!this.google || (this.isFullUpdate && !this.isDataUpdating)) {
              !this.isFilter && (this.selectedRankZones[rankZone] = true)
              _.remove(this.disabledCheckboxes, item => item === rankZone)
            }

            if (this.selectedRankZones[rankZone]) {
              const rankingUrl = getRankingUrl(item.rank, item.ranking_url)
              if (!columns.includes(rankingUrl)) {
                columns.push(rankingUrl)
              }
              const position = item.rank >= 1 && item.rank <= 100 ? item.rank : item.rank === 404 ? maxRank + (notRankingEnabled ? 20 : 10) : maxRank + 10
              data.push([moment(item.snapshot_date).toDate(), position, rankingUrl])
              xTicks.push(item.snapshot_date)
            }
          })
        } else {
          const selectedWebProperties = [this.webPropertyGroupId, ...this.selectedComparisonWebPropertyGroupIds]
          if (this.selectedComparisonWebPropertyGroupIds.length !== 1) {
            columns = selectedWebProperties.map(item => this.webPropertyGroupsOptions.find(i => i.value === item).label)
          }

          keywordHistory.forEach(item => {
            if (!xTicks.includes(item.snapshot_date)) {
              xTicks.push(item.snapshot_date)
            }

            if (this.selectedComparisonWebPropertyGroupIds.length === 1) {
              const rankingUrl = getRankingUrl(item.rank, item.ranking_url)
              if (!columns.includes(rankingUrl)) {
                columns.push(rankingUrl)
              }
              if (!objectOfWebproperties[item.web_property]) {
                objectOfWebproperties[item.web_property] = [rankingUrl]
              } else {
                objectOfWebproperties[item.web_property].push(rankingUrl)
              }
              if (item.web_property === this.webPropertyGroupId && !mappedColumns[0].includes(rankingUrl)) {
                mappedColumns[0].push(rankingUrl)
              } else if (
                !mappedColumns[1].includes(rankingUrl) &&
                (!mappedColumns[0].includes(rankingUrl) || ['NR', 'ND'].includes(rankingUrl))
              ) {
                mappedColumns[1].push(rankingUrl)
              }
            }
          })

          columns = columns.sort(sortColumns)
          mappedColumns[0] = mappedColumns[0].sort(sortColumns)
          mappedColumns[1] = mappedColumns[1].sort(sortColumns)

          const fillData = (webPropertySnapshot, dataRow) => {
            if (webPropertySnapshot) {
              const rankZone = getRankZone(webPropertySnapshot.rank)
              if (!this.google || (this.isFullUpdate && !this.isDataUpdating)) {
                !this.isFilter && (this.selectedRankZones[rankZone] = true)
                _.remove(this.disabledCheckboxes, item => item === rankZone)
              }
              dataRow.push(this.selectedRankZones[rankZone]
                ? webPropertySnapshot.rank === 404
                  ? maxRank + (notRankingEnabled ? 20 : 10)
                  : webPropertySnapshot.rank > 100
                    ? maxRank + 10
                    : webPropertySnapshot.rank
                : null)
            } else {
              dataRow.push(null)
            }
          }

          xTicks.forEach(item => {
            const dataRow = [moment(item).toDate()]
            const dateSnapshots = keywordHistory.filter(i => i.snapshot_date === item)
            if (this.selectedComparisonWebPropertyGroupIds.length !== 1) {
              selectedWebProperties.forEach(item => {
                const webPropertySnapshot = dateSnapshots.find(i => i.web_property === item)
                fillData(webPropertySnapshot, dataRow)
              })
            } else {
              const webPropertyLinks = Object.entries(objectOfWebproperties)

              const primaryDataRow = []
              mappedColumns[0].forEach(url => {
                const webProperty = webPropertyLinks.find(([webProperty, arrOfLinks]) => arrOfLinks.includes(url))
                const webPropertySnapshot = dateSnapshots.find(
                  i => i.ranking_url === url && i.web_property === webProperty[0]
                )
                fillData(webPropertySnapshot, primaryDataRow)
              })
              const primarySnapshotRank = primaryDataRow.find(i => i) || null
              dataRow.push(primarySnapshotRank)
              primaryDataRow.forEach(rank => dataRow.push(rank))

              const comparisonDataRow = []
              mappedColumns[1].forEach(url => {
                const webProperty = webPropertyLinks.find(([webProperty, arrOfLinks]) => arrOfLinks.includes(url))
                const webPropertySnapshot = dateSnapshots.find(
                  i => i.ranking_url === url && i.web_property === webProperty[0]
                )
                fillData(webPropertySnapshot, comparisonDataRow)
              })
              const snapshotRank = comparisonDataRow.find(i => i) || null
              dataRow.push(snapshotRank)
              comparisonDataRow.forEach(rank => dataRow.push(rank))
            }
            data.push(dataRow)
          })
        }

        xTicks = xTicks.sort()
        xTicks = xTicks.map((item, index) => {
          const date = moment(item).toDate()
          if (index === 0 || index === xTicks.length - 1) {
            return { v: date, f: date }
          }
          return { v: date, f: '' }
        })

        !this.isDataUpdating && (this.isFullUpdate = false)
        columns = columns.sort(sortColumns)
        mappedColumns[0] = mappedColumns[0].sort(sortColumns)
        mappedColumns[1] = mappedColumns[1].sort(sortColumns)

        if (noDataEnabled) {
          this.pointColors = this.pointColors.slice(0, columns.length - 1)
          this.pointColors.push(ND_COLLOR)
        }

        return {
          xTicks,
          data,
          columns,
          mappedColumns,
          yTicks,
          minRank: minRank !== 0 ? minRank : 1
        }
      },
      keywordHistory: 'keywordHistory'
    }),
    primaryColumns () {
      const selectedComparisonLength = this.selectedComparisonWebPropertyGroupIds.length
      return selectedComparisonLength === 1
        ? this.chartData.mappedColumns[0]
        : selectedComparisonLength >= 2
          ? [this.chartData.columns[0]]
          : this.chartData.columns
    },
    compareColumns () {
      const compareColumns = _.cloneDeep(this.chartData.columns)
      compareColumns.shift()
      return this.selectedComparisonWebPropertyGroupIds.length === 1
        ? this.chartData.mappedColumns[1]
        : compareColumns
    },
    title () {
      return _.get(this, 'keyword', '') || ''
    },
    notEnoughData () {
      const dataExist = this.chartData &&
        this.chartData.columns &&
        this.chartData.columns.length &&
        !_.get(this, 'keywordHistory[0].is_info_response', true)
      let rankZoneSelected = true
      if (this.google) {
        rankZoneSelected = Object.values(this.selectedRankZones).includes(true)
      }
      return !dataExist || !rankZoneSelected
    },
    msv () {
      return _.get(this.keywordHistory, '[0].msv', []) || []
    },
    mappedUrl () {
      return _.get(this.keywordHistory, '[0].published_url', '') || _.get(this.keywordHistory, '[0].mapped_url', '') || ''
    },
    diffScore () {
      return _.get(this.keywordHistory, '[0].diff_score', '') || ''
    },
    mappedUrlOptions () {
      const options = _.cloneDeep(this.mappedUrls).map(i => ({ label: i.label, value: i._id }))
        .map(opt => {
          // const clientWebPropMatch = this.webProperties.find(prop => prop.type === 'Clients' && opt.label.includes(prop.domain))
          // if (clientWebPropMatch) {
          opt.attributes = [{ 'private-content': true }]
          // }
          return opt
      })
      options.unshift({ label: 'Remove URL', value: -1 })
      return options
    },
    isPublished () {
      return _.get(this.keywordHistory, '[0].is_published', false) || false
    },
    serpFeatures () {
      return _.get(this.keywordHistory, '[0].serp_features', []) || []
    },
    urlStatus () {
      return _.get(this.keywordHistory, '[0].url_status', '') || ''
    },
    contentBriefId () {
      return _.get(this.keywordHistory, '[0].content_brief_id', '') || ''
    },
    screenshotName () {
      return `${this.selectedClient.name.replace(/ /g, '-')}_${this.title.replace(/ /g, '-')}_movement-history.png`
    },
    isComparing () {
      return !!this.selectedComparisonWebPropertyGroupIds.length
    },
    keywordData () {
      return _.cloneDeep(this.msv)
    }
  },
  methods: {
    ...mapActions('rankZoneKeywordMovementHistory', ['getKeywordHistory', 'resetKeywordHistory', 'updateKeywordData', 'hashKeywordHistoryModal']),
    async loadData (isFullUpdate = false) {
      this.isDataUpdating = true
      await this.getKeywordHistory({
        clientId: this.clientId,
        keyword: this.keyword
      })
      this.relevancy = String(_.get(this.keywordHistory, '[0].score', 0) || 0)
      this.mappedUrlId = _.get(this.keywordHistory, '[0].mapped_url_id', '') || ''
      this.isDataUpdating = false
      this.isDataFetched = true
      this.isFullUpdate = isFullUpdate
      if (isFullUpdate) this.pointColors = [...POINT_COLORS]
    },
    recalculateKeywordMaxWidth () {
      if (!this.$refs['main-info-header']) {
        return
      }
      const keywordWrapperWidth = this.$refs['main-info-header'].clientWidth
      this.headerKeywordMaxWidth = keywordWrapperWidth - ((this.$refs.serp ? this.$refs.serp.clientWidth : 0) + 180)
    },
    async updateKeywordUrlAndScore (type, diffScore = 0) {
      this.isDataUpdated = true
      const params = {
        clientId: this.clientId,
        keyword_id: _.get(this.keywordHistory, '[0].id', '') || ''
      }
      if (type === 'url') {
        params.url_id = this.mappedUrlId
        params.successMessage = 'Successfully updated mapped url'
      } else if (type === 'score') {
        params.score = this.relevancy
        params.successMessage = 'Successfully updated relevancy score'
      } else if (type === 'diffScore') {
        params.diff_score = diffScore
        params.successMessage = 'Successfully updated difficult score'
      }
      return this.updateKeywordData(params)
    },
    selectLink (index) {
      // plus 3 because y series start from 3 index
      const columnNumber = index + 3
      let selectedLinks = _.cloneDeep(this.chart.getSelection())
      let selectNewColumn = true
      selectedLinks = selectedLinks.filter(item => {
        if (item.column === columnNumber) {
          selectNewColumn = false
          return false
        } else {
          return true
        }
      })
      selectNewColumn && selectedLinks.push({ row: null, column: columnNumber })
      this.chart.setSelection(selectedLinks)
    },
    async getModalPath () {
      let path = `${window.location.origin}/dashboard#/reports/keyword-movement-report?modal=`
      const params = {
        tempComparisonArray: this.comparison,
        keyword: this.keyword,
        clientId: this.clientId,
        webPropertyGroupId: this.webPropertyGroupId,
        selectedRankZones: _.cloneDeep(this.selectedRankZones),
      }
      let viewId = null
      try {
        viewId = await this.hashKeywordHistoryModal({ clientId: this.clientId, type: 'keyword_movement_history_modal', params })
      } catch (error) {
        ToasterService.showToast(ToasterService.ERROR_TYPE, 'Something went wrong!')
      }
      if (viewId) {
        return path + viewId
      }
      return null
    },
    async copyModal () {
      const path = await this.getModalPath()
      this.$copyText(path)
        .then(
          e => {
            ToasterService.showToast(ToasterService.INFO_TYPE, 'Filters copied to clipboard')
          },
          // eslint-disable-next-line handle-callback-err
          err => {
            ToasterService.showToast(ToasterService.ERROR_TYPE, 'Cannot copy filters')
          }
        )
    },
    close () {
      this.$emit('close', this.isDataUpdated)
    },
    async exportCsv () {
      this.isExportCSV = true
      const getRankZone = (rank) => {
        switch (true) {
          case rank <= 3:
            return '01 to 03'
          case rank <= 7:
            return '04 to 07'
          case rank <= 10:
            return '08 to 10'
          case rank <= 25:
            return '11 to 25'
          case rank <= 50:
            return '26 to 50'
          case rank <= 100:
            return '51 to 100'
          case rank === 404:
            return 'ND'
          default:
            return 'NR'
        }
      }
      const handler = (columns) => {
        return item => {
          const row = []
          columns.forEach(name => {
            if (name === 'snapshot_date') {
              row.push(moment(item[name]).format('YYYY-MM-DD'))
            } else if (name === 'rank_zone') {
              row.push(getRankZone(item.rank))
            } else if (name === 'content_brief_id') {
              row.push(`"${window.location.origin}/dashboard#/content-briefs/${item.content_brief_id}/${this.isPublished ? 'progress' : 'strategy'}"`)
            } else if (name === 'ranking_url') {
              row.push(item[name] ? item[name] : item.rank === 404 ? 'ND' : 'NR')
            } else if (name.includes('msv')) {
              const index = parseInt(name.split('_')[1])
              row.push(this.msv[index][1])
            } else if (name === 'web_properties') {
              webProperties.forEach(i => {
                row.push(item[`${i.value}_rank`])
                row.push(item[`${i.value}_ranking_url`] ? item[`${i.value}_ranking_url`] : item[`${i.value}_rank`] === 404 ? 'ND' : 'NR')
              })
            } else if (name === 'keyword_movement_modal') {
              row.push(`"${this.getModalPath()}"`)
            } else {
              row.push(item[name] ? item[name] : 'NR')
            }
          })
          data.push(row)
        }
      }

      let header = []
      let columnNames = []
      let webProperties = []

      if (!this.isComparing) {
        header = ['"Keyword"', '"Score"', ...this.msv.map(i => `"MSV ${i[0]}"`), '"Rank Report Date"',
          '"Rank Zone"', '"Rank"', '"Ranking URL"', '"Current Mapped URL"', '"Mapped URL Content Brief"', '"Keyword Movement Modal"']
        columnNames = ['keyword', 'score', ...this.msv.map((i, index) => `msv_${index}`), 'snapshot_date',
          'rank_zone', 'rank', 'ranking_url', 'mapped_url', 'content_brief_id', 'keyword_movement_modal']
      } else {
        const selectedWebProperties = [this.webPropertyGroupId, ...this.selectedComparisonWebPropertyGroupIds]
        webProperties = selectedWebProperties.map(item => this.webPropertyGroupsOptions.find(i => i.value === item)).sort((a, b) => {
          if ((a.type === 'Clients' && b.type === 'Siblings') || (a.type === 'Siblings' && b.type === 'Competitors')) {
            return -1
          } else if ((b.type === 'Clients' && a.type === 'Siblings') || (b.type === 'Siblings' && a.type === 'Competitors')) {
            return 1
          } else {
            return 0
          }
        })
        header = ['"Rank Report Date"', '"Keyword"', ...this.msv.map(i => `"MSV ${i[0]}"`), '"Score"',
          ...webProperties.reduce((arr, i) => {
            arr.push(`"${i.label} ${i.type.slice(0, -1)} Rank"`)
            arr.push(`"${i.label} ${i.type.slice(0, -1)} URL"`)
            return arr
          }, []), '"Current Mapped URL"', '"Mapped URL Content Brief"', '"Keyword Movement Modal"'
        ]
        columnNames = ['snapshot_date', 'keyword', ...this.msv.map((i, index) => `msv_${index}`), 'score',
          'web_properties', 'mapped_url', 'content_brief_id', 'keyword_movement_modal']
      }

      const data = []
      data.push(header)

      let snapshots = []
      if (!this.isComparing) {
        snapshots = _.cloneDeep(this.keywordHistory).filter(i => i.web_property === this.webPropertyGroupId)
      } else {
        snapshots = _.cloneDeep(this.keywordHistory).filter(i => i.web_property === this.webPropertyGroupId || this.selectedComparisonWebPropertyGroupIds.includes(i.web_property))
        const snapshotDates = this.chartData.xTicks.map(i => moment(i.v).format('YYYY-MM-DD'))
        snapshots = snapshotDates.map(item => {
          const data = { snapshot_date: item }
          const dateSnapshots = snapshots.filter(i => i.snapshot_date === item)
          data.keyword = dateSnapshots[0].keyword
          data.content_brief_id = dateSnapshots[0].content_brief_id
          data.score = dateSnapshots[0].score
          data.mapped_url = dateSnapshots[0].mapped_url
          webProperties.forEach(item => {
            const webPropertySnapshot = dateSnapshots.find(i => i.web_property === item.value)
            data[`${webPropertySnapshot.web_property}_rank`] = webPropertySnapshot.rank
            data[`${webPropertySnapshot.web_property}_ranking_url`] = webPropertySnapshot.ranking_url
          })

          return data
        })
      }
      snapshots.sort((a, b) => {
        const snapshotDate1 = moment(a.snapshot_date).toDate()
        const snapshotDate2 = moment(b.snapshot_date).toDate()
        if (snapshotDate1 < snapshotDate2) {
          return -1
        }
        if (snapshotDate1 > snapshotDate2) {
          return 1
        }
        return 0
      })

      snapshots.forEach(handler(columnNames))
      csvExport(`${this.selectedClient.name.replace(/ /g, '-')}_${this.keyword.replace(/ /g, '-')}_Movement-Rank-Report`, data)
      this.isExportCSV = false
    },
    /* the chart should redraw after resizing browser window */
    resizeListener () {
      this.debounced.flush()
      this.debounced()
    },
    /**
     * Callback for createChart property of vue-google-charts library
     * Consider isStacked property in options object manages the way of rendering the chart
     * {@see https://developers.google.com/chart/interactive/docs/gallery/scatterchart#data-format}
     * all list of available options {@see https://developers.google.com/chart/interactive/docs/gallery/scatterchart#data-format}
     * prepared data passed into chat using DataView {@link https://developers.google.com/chart/interactive/docs/datatables_dataviews}
     * @param el - chart html element
     * @param google - link to the Google Chart library
     */
    createChart (el, google) {
      if (!google || !el || !this.chartData || !this.chartData.data.length) {
        return
      }

      this.google = google
      this.el = el

      const createCustomTooltip = (date, rank, url) => {
        const rankText = url === 'NR' ? 'NR' : url === 'ND' ? 'ND' : rank
        return `
          <div class="keyword-history__tooltip">
            <p><b>Rank:</b> ${rankText}</p>
            <p><b>URL:</b> ${url}</p>
            <p><b>Date:</b> ${moment(date).format('YYYY-MM-DD')}</p>
          </div>
        `
      }

      const dataTable = new google.visualization.DataTable()

      dataTable.addColumn('date', 'Snapshot Date')
      if (!this.isComparing) {
        dataTable.addColumn({ type: 'string', role: 'tooltip', p: { html: true } })
        // column for connect all dots with line don't visible on chart
        dataTable.addColumn('number', '')
        this.chartData.columns.forEach(url => {
          dataTable.addColumn('number', url)
        })
      } else if (this.selectedComparisonWebPropertyGroupIds.length === 1) {
        // dataTable.addColumn({ type: 'string', role: 'tooltip', p: { html: true } })
        // column for connect all dots with line don't visible on chart
        dataTable.addColumn('number', '')
        this.chartData.mappedColumns[0].forEach(url => {
          dataTable.addColumn('number', url)
        })
        dataTable.addColumn('number', '')
        this.chartData.mappedColumns[1].forEach(url => {
          dataTable.addColumn('number', url)
        })
      } else {
        this.chartData.columns.forEach(i => {
          dataTable.addColumn('number', i)
        })
      }

      if (!this.isComparing) {
      // if (this.selectedComparisonWebPropertyGroupIds.length < 2) {
        const rows = []
        this.chartData.data.forEach(snapshot => {
          const mappedSnapshot = []
          const mapUrl = url => {
            if (url === snapshot[2]) {
              mappedSnapshot.push(snapshot[1])
            } else {
              mappedSnapshot.push(null)
            }
          }

          mappedSnapshot.push(snapshot[0])
          if (!this.isComparing) {
            mappedSnapshot.push(createCustomTooltip(snapshot[0], snapshot[1], snapshot[2]))
          }
          mappedSnapshot.push(snapshot[1])
          if (!this.isComparing) {
            this.chartData.columns.forEach(mapUrl)
          } else {
            this.chartData.mappedColumns[0].forEach(mapUrl)
            if (this.chartData.mappedColumns[1].length) {
              mappedSnapshot.push(snapshot[1])
              this.chartData.mappedColumns[1].forEach(mapUrl)
            }
          }
          rows.push(mappedSnapshot)
        })
        dataTable.addRows(rows)
      } else {
        dataTable.addRows(this.chartData.data)
      }

      const series = this.selectedComparisonWebPropertyGroupIds.length < 2
        ? [
          { visibleInLegend: false, color: '#000', pointsVisible: false, lineWidth: 2 },
          ...this.pointColors.map(color => ({ color, lineWidth: 0 }))
        ]
        : this.pointColors.map(color => ({ color }))

      if (this.selectedComparisonWebPropertyGroupIds.length === 1) {
        series[this.chartData.mappedColumns[0].length + 1] = {
          ...series[this.chartData.mappedColumns[0].length + 1],
          pointsVisible: false,
          lineWidth: 2
        }
      }

      const options = {
        backgroundColor: '#fff',
        height: 400,
        width: '100%',
        interpolateNulls: true,
        chartArea: { width: '100%', height: '80%', left: 24, top: 10 },
        legend: { position: 'none' },
        fontName: 'Roboto',
        hAxis: {
          ticks: [...this.chartData.xTicks],
          format: 'y-M-d',
          textStyle: {
            color: '#b2b9be',
            fontSize: 10
          }
        },
        pointSize: 10,
        vAxis: {
          ticks: [...this.chartData.yTicks],
          viewWindow: {
            min: this.chartData.minRank
          },
          baselineColor: 'transparent',
          textStyle: {
            color: '#b2b9be',
            fontSize: 12,
            fontWeight: 500,
            bold: true
          },
          direction: -1
        },
        series,
        fontSize: 12,
        selectionMode: 'multiple',
        title: 'none'
      }

      if (!this.isComparing) {
        Object.assign(options, { tooltip: { trigger: 'focus', isHtml: true }, focusTarget: 'category' })
      } else {
        Object.assign(options, { tooltip: { trigger: 'none' } })
      }

      const chart = !this.isComparing ? new google.visualization.ScatterChart(el) : new google.visualization.LineChart(el)
      chart.draw(dataTable, options)
      this.chart = chart
      this.chartDrawed = true
    },
    onScreenShotStatusChanged (status) {
      this.isScreenshotTaking = status
    },
    handleLinksWrapperResize (e) {
      if (!this.isComparing) {
        return
      }
      this.$nextTick(() => {
        const clientWidth = e.target.clientWidth
        this.comparisonPaddingRight = `${clientWidth && clientWidth > 84 ? clientWidth - 84 : 0}px`
      })
    }
  }
}
</script>

<style scoped lang="scss">
  .keyword-history {
    padding: 40px;
    display: flex;
    flex-direction: column;
    position: relative;

    &__serp-features {
    }

    &__no-data {
      display: flex;
      height: 400px;
      background-color: #fff;
      border-radius: $rfx-default-border-radius;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
    }

    &::v-deep &__tooltip {
      padding: 0 5px;
    }

    &__diff-score {
      margin-top: 0 !important;
      position: relative;

      &::v-deep input {
        height: 38px;
        padding: 0 0 0 15px !important;
        width: 80px;
        border: 1px solid $rfx-light-steel !important;
        font-size: 14px !important;
        font-weight: 400 !important;
      }

      &::v-deep .page-title__action-buttons {
        right: 0 !important;
      }
    }

    &__keyword-data {
      display: flex;
      margin: 0 20px;
      align-items: center;

      &_mapped-url-redirect {
        font-size: 20px;
        position: absolute;
        margin-left: 14px;
        margin-top: -7px;
        color: $rfx-dark-march;

        &:hover {
          color: $rfx-bright-blue;;
        }
      }

      &_score,
      &_diff-score {
        margin-right: auto;
      }

      &_select {
        flex-direction: column;
        align-items: flex-start;

        .keyword-history__keyword-data_header {
          line-height: 2;
        }

        &_wrapper {
          display: flex;
          margin-top: 24px;
          width: -webkit-fill-available;
        }
      }

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      &_header {
        font-size: 12px;
        font-weight: 500;
        color: $rfx-dark-march;
        line-height: 16px;
        margin-right: 16px;
      }

      &_count {
        font-size: 14px;
        color: $rfx-dark-march;
        line-height: 16px;
      }
    }

    &__header {
      margin-top: -4px;

      &_url-info {
        margin-top: 16px;
      }

      &_keyword-snapshot-data {
        margin-top: 16px;
        align-items: flex-start;
      }

      &_main-info {
        display: flex;
        align-items: center;
        position: relative;
        width: 100%;
        color: $rfx-dark-march;

        a {
          text-decoration: none;

          &:hover {
            color: $rfx-bright-blue;
            text-decoration: underline;
            cursor: pointer;
          }
        }

        &_column {
          flex-direction: column;
        }

        &_url-data {
          width: fit-content;
          margin-top: 11px;
          font-size: 14px;
          position: relative;
          color: $rfx-dark-march;

          &_wrapper {
            display: flex;
            flex-direction: column;
            margin-right: auto;
            justify-content: space-between;
          }
        }
      }
    }

    &__diagram {
      margin-top: 34px;

      &-hidden {
        height: 0px;
        overflow: hidden;
        visibility: hidden;
      }
    }

    &__scatter-chart {
      &::v-deep svg {
        overflow: visible !important;
      }
    }

    &__title {
      text-align: center;
      font-size: 20px;
      line-height: 20px;
      color: $rfx-dark-march;
      font-weight: bold;
      margin-right: 50px;

      &__search {
        margin-top: 0 !important;
        position: relative !important;
        padding-left: 0 !important;
        top: 3px;
        left: -2px;

        &::v-deep .search-in-google__link {
          font-size: 20px;
        }
      }
    }

    &__subtitle {
      text-align: center;
      font-size: 16px;
      line-height: 23px;
      color: $rfx-light-march;
    }

    &__buttons {
      position: absolute;
      top: 36px;
      right: 30px;
      font-size: 18px;
      color: $rfx-bright-blue;
      display: flex;

      &_disabled {
        cursor: default;
        opacity: 0.4;
        pointer-events: none;
      }
    }

    &__open-content-brief {
      cursor: pointer;
      font-size: 20px;
      display: inline-block;
      color: $rfx-dark-march;
      position: absolute;
      top: 9px;

      &:hover {
        color: $rfx-bright-blue;
      }
    }

    &__copy {
      cursor: pointer;
      color: $rfx-dark-march;
      margin-right: 8px;

      &:hover {
        color: $rfx-bright-turquoise;
      }
    }

    &__loader {
      display: flex;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px)
    }

    &__loader-container {

    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      margin: 20px 20px 20px auto;
      cursor: pointer;

      &_close {
        background-color: $rfx-medium-march;
        color: #fff;
      }
    }

    &__separator {
      position: relative;
      left: -40px;
      margin: 16px 0;
      height: 1px;
      width: calc(100% + 80px);
      background-color: #e6e8ea;
    }

    &__web-property-select,
    &__multi-select {
      width: 200px;

      &_url {
        width: 400px;
      }

      &_score {
        width: 80px;
      }
    }

    &__checkboxes {
      margin-top: -50px;
      position: relative;
      z-index: 100;
      width: 100%;
      display: flex;
      justify-content: center;
      padding: 8px 0;
    }

    &__screenshot {
      font-size: 21px;
      cursor: pointer;
      color: $rfx-dark-march;
      padding-top: 3px;
      margin-right: 10px;

      &:hover {
        color: $rfx-bright-turquoise;
      }
    }

    &__checkbox {
      width: fit-content;
      margin: 0 15px;

      &-disabled {
        pointer-events: none;
      }
    }

    &__links {
      width: fit-content;
      display: flex;
      min-width: 84px;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;

      &_row {
        flex-direction: row;
        width: -webkit-fill-available;
        justify-content: center;
      }

      &_comparing {
        justify-content: space-between !important;
        padding: 0 100px !important;

        .keyword-history__link_header {
          padding-left: 19px;
        }

        .keyword-history__links {
          flex: 1;
        }
      }
    }

    &__link {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 0 5px;

      &_header {
        font-size: 12px;
        line-height: 24px;
        color: $rfx-dark-march;
        font-weight: bold;
      }

      &__color {
        width: 8px;
        min-width: 8px;
        height: 8px;
        min-height: 8px;
        border-radius: 50%;
        margin: 5px 5px 0 0;
      }

      a {
        text-decoration: none;
        cursor: pointer;
        color: #000000;
        word-break: break-word;
        margin-bottom: 5px;

        &:hover {
          color: $rfx-bright-blue !important;
          text-decoration: underline;
        }

        &:visited {
          color: #000000;
        }
      }
    }
  }
</style>
